import { Building2, Home, Car, Clock, PoundSterling } from 'lucide-react';
import { formatAddress } from '../../utils/format';
import { CATEGORIES } from '../../lib/categories';

const SERVICE_TYPES = [
  { id: 'salon', name: 'Salon / Studio' },
  { id: 'home', name: 'Home-based' },
  { id: 'mobile', name: 'Mobile' }
];

const formatDuration = (mins) => {
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem ? `${hrs}h ${rem}m` : `${hrs}h`;
  }
  return `${mins} min`;
};

export function ReviewStep({ form }) {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-2">Review your profile</h2>
        <p className="text-stone-500">Make sure everything looks good before submitting.</p>
      </div>

      <div className="space-y-4 p-6 bg-[#F9F6F0] rounded-lg">
        {form.profile_image && (
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#D4AF37]">
              <img src={form.profile_image} alt="Profile" className="w-full h-full object-cover" />
            </div>
            <span className="text-sm text-stone-500">Profile Picture</span>
          </div>
        )}
        <div>
          <span className="text-sm text-stone-500">Business Name</span>
          <p className="font-medium text-[#1A1A1A]">{form.brand_name}</p>
        </div>
        <div>
          <span className="text-sm text-stone-500">Your Zuriya Link</span>
          <p className="font-mono text-[#D4AF37]">zuriya.co.uk/@{form.username}</p>
        </div>
        <div>
          <span className="text-sm text-stone-500">Bio</span>
          <p className="text-[#1A1A1A]">{form.bio}</p>
        </div>
        <div>
          <span className="text-sm text-stone-500">Area</span>
          <p className="text-[#1A1A1A]">{form.location}</p>
        </div>
        <div>
          <span className="text-sm text-stone-500">Service Types</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {form.service_types.map((type) => (
              <span key={type} className="px-3 py-1 bg-[#1A1A1A] text-white rounded-full text-sm">
                {SERVICE_TYPES.find(t => t.id === type)?.name}
              </span>
            ))}
          </div>
        </div>
        {form.service_types.includes('salon') && form.salon_address && (
          <div>
            <span className="text-sm text-stone-500 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> Salon / Studio Address
            </span>
            <p className="text-[#1A1A1A]">{formatAddress(form.salon_address)}</p>
          </div>
        )}
        {form.service_types.includes('home') && form.home_address && (
          <div>
            <span className="text-sm text-stone-500 flex items-center gap-1">
              <Home className="h-3 w-3" /> Home Address
            </span>
            <p className="text-[#1A1A1A]">{formatAddress(form.home_address)}</p>
            <p className="text-xs text-stone-400 mt-0.5">Only the area will be shown to clients before booking confirmation</p>
          </div>
        )}
        {form.service_types.includes('mobile') && form.service_radius && (
          <div>
            <span className="text-sm text-stone-500 flex items-center gap-1">
              <Car className="h-3 w-3" /> Travel Radius
            </span>
            <p className="text-[#1A1A1A]">Up to {form.service_radius} {form.service_radius === 1 ? 'mile' : 'miles'}</p>
          </div>
        )}
        <div>
          <span className="text-sm text-stone-500">Categories</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {form.categories.map((cat) => (
              <span key={cat} className="px-3 py-1 bg-[#D4AF37] text-white rounded-full text-sm">
                {CATEGORIES.find(c => c.id === cat)?.name}
              </span>
            ))}
          </div>
        </div>
        {Object.values(form.social_links).some(v => v && v.trim()) && (
          <div>
            <span className="text-sm text-stone-500">Social Media</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {form.social_links.instagram && (
                <a href={`https://instagram.com/${form.social_links.instagram}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#D4AF37] hover:underline">@{form.social_links.instagram}</a>
              )}
              {form.social_links.tiktok && (
                <a href={`https://tiktok.com/@${form.social_links.tiktok}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#D4AF37] hover:underline">TikTok: @{form.social_links.tiktok}</a>
              )}
              {form.social_links.twitter && (
                <a href={`https://x.com/${form.social_links.twitter}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#D4AF37] hover:underline">X: @{form.social_links.twitter}</a>
              )}
              {form.social_links.facebook && (
                <a href={`https://facebook.com/${form.social_links.facebook}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#D4AF37] hover:underline">Facebook</a>
              )}
            </div>
          </div>
        )}
        {(form.draft_services || []).length > 0 && (
          <div>
            <span className="text-sm text-stone-500">Services ({form.draft_services.length})</span>
            <div className="space-y-2 mt-1">
              {form.draft_services.map((svc, i) => (
                <div key={svc.name || `svc-${i}`} className="flex items-center justify-between bg-white rounded-lg px-3 py-2" data-testid={`review-service-${i}`}>
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A]">{svc.name}</p>
                    <span className="text-xs text-stone-400 capitalize">{CATEGORIES.find(c => c.id === svc.category)?.name || svc.category}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-[#D4AF37] flex items-center gap-0.5"><PoundSterling className="h-3 w-3" />{svc.price}</span>
                    <span className="text-xs text-stone-400 flex items-center gap-0.5"><Clock className="h-3 w-3" />{formatDuration(svc.duration)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {(form.draft_services || []).length === 0 && (
          <div>
            <span className="text-sm text-stone-500">Services</span>
            <p className="text-xs text-stone-400 mt-1">No services added yet, you can add them from your dashboard after approval.</p>
          </div>
        )}
      </div>

      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>Note:</strong> After submitting, your profile will be reviewed by our team. 
          This usually takes 1-2 business days.
        </p>
      </div>
    </div>
  );
}
